/**
 * TanStack Query (vue-query) setup — the app-wide data cache / SWR / offline layer.
 * See `docs/adr/0001-tanstack-query-for-data-caching.md`.
 *
 * - `staleTime` 5 min: navigating back within that window serves from cache (0 requests),
 *   revalidates only when stale — the behaviour we used to hand-roll in `dashboardCache.ts`.
 * - `gcTime` long + a Preferences persister: the cache survives an app restart and a cold
 *   offline launch.
 * - online/focus are driven by Capacitor (our `useNetworkStatus` + `@capacitor/app` resume),
 *   not raw browser events, which aren't reliable inside the WebView.
 */
import { QueryClient, VueQueryPlugin, onlineManager, focusManager } from "@tanstack/vue-query";
import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Preferences } from "@capacitor/preferences";
import { App } from "@capacitor/app";
import { watch } from "vue";
import { useNetworkStatus } from "@/composables/useNetworkStatus";

const PERSIST_KEY = "coachio_query_cache_v1";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min
      gcTime: 24 * 60 * 60 * 1000, // keep around for offline cold-start
      retry: 1,
      refetchOnReconnect: true,
    },
  },
});

const persister = createAsyncStoragePersister({
  key: PERSIST_KEY,
  storage: {
    getItem: async (key) => (await Preferences.get({ key })).value,
    setItem: async (key, value) => {
      await Preferences.set({ key, value });
    },
    removeItem: async (key) => {
      await Preferences.remove({ key });
    },
  },
});

/**
 * Cache persistence wired through the VueQueryPlugin (NOT called manually) so vue-query
 * gates queries on `isRestoring` until the Preferences cache is rehydrated — otherwise
 * queries fire (and fail offline) before the cache is restored, showing an empty screen
 * on a cold offline launch.
 */
export const clientPersister = (client: QueryClient): [() => void, Promise<void>] =>
  persistQueryClient({
    queryClient: client,
    persister,
    maxAge: 24 * 60 * 60 * 1000,
  });

/** After restore: replay any mutations that were queued while offline. */
export const clientPersisterOnSuccess = (client: QueryClient): void => {
  client.resumePausedMutations();
};

/** Wire connectivity/focus to Capacitor. Call once at startup. */
export function initQueryClient(): void {
  const { isOnline } = useNetworkStatus();
  onlineManager.setOnline(isOnline.value);
  watch(isOnline, (online) => onlineManager.setOnline(online));

  // Refetch-on-focus driven by native app resume instead of browser visibility.
  focusManager.setEventListener((handleFocus) => {
    const sub = App.addListener("appStateChange", ({ isActive }) => handleFocus(isActive));
    return () => {
      sub.then((s) => s.remove());
    };
  });
}

/** Wipe the cache (in-memory + persisted). Call on login/logout to avoid cross-account leaks. */
export async function clearQueryCache(): Promise<void> {
  queryClient.clear();
  await Preferences.remove({ key: PERSIST_KEY }).catch(() => {});
}

export { VueQueryPlugin };
